import { createClient } from '@supabase/supabase-js';
import { buildWritingEngine, pickRandomOpening, BANNED_PATTERNS, SENTENCE_RHYTHM, SCENE_BEATS } from './writing-engine';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// Edtech пивот: три пресета с фиксированной длиной
export const PRESETS = [
  { id:"popscience", length:3, tone:3, style:3 }, // ~5 мин, 1080 слов
  { id:"eli8",       length:2, tone:8, style:8 }, // ~3 мин, 600 слов
  { id:"academic",   length:5, tone:7, style:7 }, // ~15 мин, 3000 слов
];

export const WORD_COUNTS = [240, 600, 1080, 1920, 3000, 4200, 6000];
export const MAX_TOKENS  = [900, 1800, 3200, 5800, 9000, 12000, 16000];

export const TONE_DESC = [
  "", // 1 — unused
  "", // 2 — unused
  "engaging and accessible like Bill Bryson — full of surprising facts, self-deprecating humor, and the genuine wonder of a curious mind explaining complex things to a smart friend", // 3 — popscience
  "", // 4 — unused
  "", // 5 — unused
  "", // 6 — unused
  "scholarly and precise, like a well-crafted academic essay — citations implied, terminology exact", // 7 — academic
  "patient and warm, like a favorite teacher explaining something genuinely exciting to a curious 8-year-old — never condescending, just radically clear. The goal is real understanding, not entertainment. Every abstract idea gets a concrete, everyday analogy (sizes, distances, feelings, things a kid would recognize) before moving on", // 8 — eli8
];

export const STYLE_DESC = [
  "", // 1 — unused
  "", // 2 — unused
  "clear and vivid, mixing plain explanation with occasional surprising analogies — Bill Bryson style", // 3 — popscience
  "", // 4 — unused
  "", // 5 — unused
  "", // 6 — unused
  "strictly academic — precise terminology, complex syntax, no concessions to readability", // 7 — academic
  "extremely simple vocabulary, short sentences, one idea at a time — never two new concepts in the same sentence. Use 'imagine if...' and 'it's kind of like...' constructions liberally. Zero jargon; if a technical term is unavoidable, immediately explain it in plain words right after", // 8 — eli8
];

export const PRESET_COLORS = {
  popscience: "#4A7A9B",
  academic:   "#5C7A6E",
  eli8:       "#7A5C8B",
};

const C = {
  bg:"#F7F5F2", surface:"#EDE9E3", surface2:"#D9D3C7", border:"#C8C2B4",
  accent:"#5C7A6E", accentDim:"#7a9e92", text:"#2a2620", text2:"#6a6050",
  text3:"#b0a898", btnBg:"#5C7A6E", btnText:"#F7F5F2", ornament:"#C4774A",
  redBg:"#fdf0e8", redBorder:"#e8c8a8", red:"#a03010", inputBg:"#FDFCFA",
};

// ── Helpers ────────────────────────────────────────────────────────────────────

export function parseWikiInput(raw) {
  const s = raw.trim();
  try {
    const url = new URL(s);
    const m = url.hostname.match(/^([a-z-]+)\.wikipedia\.org$/);
    if (m) {
      const t = url.pathname.match(/\/wiki\/(.+)/);
      if (t) return { lang:m[1], topic:decodeURIComponent(t[1]).replace(/_/g," "), isUrl:true };
    }
  } catch {}
  return { lang:"ru", topic:s, isUrl:false };
}

export async function searchArticles(topic, uiLang="ru") {
  const wikiHost = `${uiLang}.wikipedia.org`;
  try {
    const searchRes = await fetch(`https://${wikiHost}/w/api.php?` + new URLSearchParams({
      action:"query", list:"search", srsearch:topic,
      format:"json", origin:"*", srlimit:"6", srnamespace:"0",
    }));
    const sd = await searchRes.json();
    const results = sd.query?.search || [];
    if (results.length <= 1) return null;
    const q = topic.toLowerCase();
    const titleMatches = results.filter(r => r.title.toLowerCase().includes(q));
    if (titleMatches.length < 2) return null;
    const titles = titleMatches.slice(0,5).map(r=>r.title).join("|");
    const descRes = await fetch(`https://${wikiHost}/w/api.php?` + new URLSearchParams({
      action:"query", prop:"description", titles, format:"json", origin:"*",
    }));
    const dd = await descRes.json();
    const pages = Object.values(dd.query?.pages || {});
    return titleMatches.slice(0,5).map(r => {
      const page = pages.find(p=>p.title===r.title) || {};
      return {
        title: r.title,
        description: page.description || r.snippet?.replace(/<[^>]+>/g,"").slice(0,100) || "",
        url: `https://${wikiHost}/wiki/${encodeURIComponent(r.title.replace(/ /g,"_"))}`,
      };
    });
  } catch { return null; }
}

export async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {};
}

export async function streamFacts(topic, uiLang="ru", onFact, abortRef) {
  const langLine = uiLang==="ru" ? "Write all facts in Russian." : "Write all facts in English.";
  const prompt = `Search Wikipedia for "${topic}" and find 5 fascinating, surprising facts.
${langLine}
Always write years, dates, ages, and quantities as digits (e.g. "1789", "300 soldiers"), never spell them out as words.
Reply ONLY with a JSON array, no markdown, no extra text, no citation tags, no HTML:
[{"label":"1-2 word label","fact":"one sentence"},{"label":"...","fact":"..."},{"label":"...","fact":"..."},{"label":"...","fact":"..."},{"label":"...","fact":"..."}]`;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch("/api/generate", {
      method:"POST", headers:{"Content-Type":"application/json", ...authHeaders},
      body:JSON.stringify({ model:"claude-haiku-4-5-20251001", max_tokens:500,
        tools:[{type:"web_search_20250305", name:"web_search"}],
        messages:[{role:"user", content:prompt}] }),
    });
    if (!res.ok) return;
    const data = await res.json();
    const text = data.content.filter(b=>b.type==="text").map(b=>b.text).join("");
    const m = text.match(/\[[\s\S]*\]/);
    if (!m) return;
    const facts = JSON.parse(m[0]);
    const stripTags = s => s.replace(/<cite[^>]*>/g,'').replace(/<\/cite>/g,'').replace(/<[^>]+>/g,'').trim();
    for (let i = 0; i < facts.length; i++) {
      if (abortRef?.current) return;
      if (facts[i]?.label && facts[i]?.fact) {
        if (i > 0) { await new Promise(r => setTimeout(r, 5500)); if (abortRef?.current) return; }
        onFact({ label: stripTags(facts[i].label), fact: stripTags(facts[i].fact) });
      }
    }
  } catch {}
}

export async function generateStory(raw, { length, tone, style, uiLang="ru", presetId=null }) {
  const { lang, topic, isUrl } = parseWikiInput(raw);
  const words = WORD_COUNTS[length - 1];
  const wikiHost = isUrl ? `${lang}.wikipedia.org` : `${uiLang}.wikipedia.org`;
  const storyLang = isUrl ? lang : uiLang;
  const langLine = storyLang==="ru" ? "Write the entire story in Russian."
    : storyLang==="en" ? "Write the entire story in English."
    : `Write the entire story in the same language as the article (lang: ${storyLang}).`;
  const searchInstruction = isUrl
    ? `Search the web for: ${raw.trim()} and read the Wikipedia article`
    : `Search the web for: site:${wikiHost} ${topic} — find and read the Wikipedia article about ${topic}`;

  const writingEngine = buildWritingEngine(presetId);
  const opening = pickRandomOpening();

  // Best-effort: fetch images already used in the Wikipedia article by topic name,
  // before generation. If the model resolves to a differently-titled article, or
  // the fetch fails, we simply proceed with zero images — never blocks the story.
  let images = [];
  try {
    images = await getArticleImages(topic, storyLang);
  } catch { /* images are optional */ }

  const imageInstruction = images.length
    ? (storyLang === "ru"
        ? `\n\nДоступные изображения из статьи (номер: описание) — вставляй маркер [[IMG:n]] в начале абзаца, к которому изображение подходит по смыслу. Постарайся использовать минимум 2 из них по всей истории, распределив их по разным частям текста (не только в начале), а не концентрируясь на одном месте. Каждое изображение — максимум один раз:\n${images.map((img, i) => `${i}: ${img.caption || "(без описания)"}`).join("\n")}`
        : `\n\nAvailable images from the article (number: caption) — insert an [[IMG:n]] marker at the start of the paragraph it best fits thematically. Aim to use at least 2 of them across the story, spread across different parts of the text (not clustered at the start). Use each image at most once:\n${images.map((img, i) => `${i}: ${img.caption || "(no caption)"}`).join("\n")}`)
    : "";

  const prompt = `${searchInstruction}

${writingEngine}

${BANNED_PATTERNS}

${SENTENCE_RHYTHM}

${SCENE_BEATS}

## OPENING (mandatory)
${opening.instruction}

Transform it into an engaging narrative story:
- ${langLine}
- Length: aim for approximately ${words} words as a guide, but prioritize narrative coherence and a complete, satisfying arc over hitting an exact count — it's fine to go somewhat shorter or longer if the material calls for it
- Tone: ${TONE_DESC[tone-1]}
- Writing style: ${STYLE_DESC[style-1]}
- CRITICAL: Every fact, date, name must be 100% accurate — never invent anything
- COVERAGE: Do not try to cram the entire article in. Pick the most compelling thread or set of moments from the topic and tell that well, with room to breathe — a few well-told facts beat ten rushed ones. Never sacrifice narrative flow just to fit more in.
- NUMERALS: Always write years, dates, ages, and quantities as digits (e.g. "1789", "20 April 1792", "300 soldiers", "in the 18th century"), never spell them out as words — this applies in all languages
- Flowing narrative, no headers, no bullets, no lists, no dashes as list markers
- Do NOT write a title — begin the story immediately
- Do NOT include preamble like "I found the article" or "Based on Wikipedia"
- Do NOT include any meta-commentary about your own process
- Do NOT end with offers like "Would you like to know more?" or "If you'd like..."
- End with a proper concluding sentence${imageInstruction}
After the story add exactly:
[[TITLE: <exact Wikipedia article title>]]
[[URL: https://${wikiHost}/wiki/<title_with_underscores>]]
[[PULLQUOTE: <one sentence copied verbatim from your story above that would make a reader stop scrolling>]]`;

  const authHeaders = await getAuthHeaders();
  const res = await fetch("/api/generate", {
    method:"POST", headers:{"Content-Type":"application/json", ...authHeaders},
    body:JSON.stringify({ model:"claude-haiku-4-5-20251001", max_tokens:MAX_TOKENS[length-1],
      tools:[{type:"web_search_20250305", name:"web_search"}],
      messages:[{role:"user", content:prompt}] }),
  });
  if (!res.ok) {
    const err = await res.json().catch(()=>({}));
    throw new Error(err.error?.message || `API error ${res.status}`);
  }
  const data = await res.json();
  const fullText = data.content.filter(b=>b.type==="text").map(b=>b.text).join("");
  const titleMatch = fullText.match(/\[\[TITLE:\s*(.+?)\]\]/);
  const urlMatch   = fullText.match(/\[\[URL:\s*(.+?)\]\]/);
  const pullquoteMatch = fullText.match(/\[\[PULLQUOTE:\s*(.+?)\]\]/);
  let story = fullText.replace(/\[\[TITLE:[^\]]*\]\]/g,"").replace(/\[\[URL:[^\]]*\]\]/g,"").replace(/\[\[PULLQUOTE:[^\]]*\]\]/g,"");
  const servicePatterns = [
    /^(I |I've |I will |I need |Let me |Now |Based on |According to |The article |Found |Searching |Here |Allow me ).{0,250}\n/gim,
    /^(Я |Вот |Статья |Нашёл|Ищу|На основе|Согласно|Мне нужно|Мне нужен|Позвольте мне|Дайте мне|Сейчас я|Я выполню|Я попробую).{0,250}\n/gim,
    /wikipedia[^\n]*\n/gi,
    /^[^\n]{0,150}(wikipedia|article|found|search|Википедии|поиск)[^\n]*\n/gim,
    /(If you'?d? like|Would you like|I hope you enjoyed|Feel free to ask).{0,200}$/gim,
  ];
  servicePatterns.forEach(p => { story = story.replace(p,""); });
  const metaCommentaryMarkers = [
    /мне нужно/i, /мне нужен/i, /позвольте мне/i, /дайте мне/i,
    /^i need to/i, /^let me/i, /^i('ll| will| should| must)/i, /^i'm going to/i,
    /более целевой поиск/i, /targeted search/i, /full text of/i, /access the (full|complete|entire)/i,
  ];
  const hasMetaCommentary = metaCommentaryMarkers.some(p => p.test(story.slice(0, 400)));
  const listLines = (story.match(/^[-•*]\s/gm) || []).length;
  if (story.length < 100 || hasMetaCommentary || story.includes('I appreciate') || story.includes('I need to clarify') || story.includes('I cannot') || listLines > 3)
    throw new Error('Не удалось сгенерировать историю. Попробуйте ещё раз.');
  story = story.replace(/^[\s\n]+/,"").trim();
  const finalTitle = titleMatch?.[1]?.trim() || topic;
  const finalUrl = urlMatch?.[1]?.trim() || `https://${wikiHost}/wiki/${encodeURIComponent(topic)}`;
  const pullQuote = pullquoteMatch?.[1]?.trim() || null;

  // Strip any [[IMG:n]] markers for indices the model invented beyond what we sent
  story = story.replace(/\[\[IMG:(\d+)\]\]/g, (m, n) => (+n < images.length ? m : ""));

  return {
    story, title: finalTitle, url: finalUrl, images, pullQuote,
  };
}

// ── Article images ─────────────────────────────────────────────────────────────
// Fetches images already used in the Wikipedia article, in page order, via the
// public MediaWiki REST API. No auth needed. Returns [] on any failure — images
// are a nice-to-have, never block story generation.
export async function getArticleImages(title, uiLang = "ru", maxImages = 4) {
  try {
    const wikiHost = `${uiLang}.wikipedia.org`;
    const encodedTitle = encodeURIComponent(title.trim().replace(/ /g, "_"));
    const res = await fetch(`https://${wikiHost}/api/rest_v1/page/media-list/${encodedTitle}`);
    if (!res.ok) return [];
    const data = await res.json();
    const items = data.items || [];
    const images = items
      .filter(it => it.type === "image" && it.srcset?.length)
      // Drop likely-decorative files: icons, logos, maps/diagrams with no real caption,
      // and anything whose filename signals it's a UI/template asset rather than a photo.
      .filter(it => {
        const name = (it.title || "").toLowerCase();
        const hasCaption = !!it.caption?.text?.trim();
        const isJunkName = /(icon|logo|symbol|flag|coat_of_arms|locator|pictogram|\.svg$)/i.test(name);
        return hasCaption && !isJunkName;
      })
      .slice(0, 10) // gather a wider pool before picking the best few
      .map(it => {
        // srcset has multiple resolutions; pick a mid-size one, fall back to smallest
        const src = it.srcset.find(s => s.scale === "1.5x") || it.srcset.find(s => s.scale === "1x") || it.srcset[0];
        const url = src?.src?.startsWith("//") ? `https:${src.src}` : src?.src;
        return url ? { url, caption: it.caption?.text || it.title?.replace(/^File:/, "").replace(/\.\w+$/, "") || "" } : null;
      })
      .filter(Boolean)
      .slice(0, maxImages);
    return images;
  } catch {
    return [];
  }
}


// Генерит 2-3 карточки по тексту истории. Возвращает [{type, question, answer}]
// type: "blank" (пропуск в утверждении, answer = пропущенное слово) или
//       "truefalse" (утверждение, answer = "true"/"false")
// Тот же транспорт, что generateStory: /api/generate + getAuthHeaders, парсинг content[].
export async function generateFlashcards(title, story, uiLang = "ru") {
  const isRu = uiLang === "ru";
  const instr = isRu
    ? `Создай ровно 3 карточки для запоминания по этой истории — самые важные, конкретные факты (даты, числа, имена, определения).
ОБЯЗАТЕЛЬНО используй оба типа карточек, не только один:
1. "blank" — утверждение с пропущенным ключевым словом (пропуск обозначь как ___), "answer" — пропущенное слово, 1-4 слова
2. "truefalse" — короткое утверждение по тексту истории (не более 25 слов включая вводную фразу и "Правда или ложь?"), сформулированное как самостоятельный вопрос-загадка, понятный без контекста истории (человек может увидеть только эту карточку, например в соцсетях, и не знать больше ничего) — начни с интригующей формулировки типа "Знаешь ли ты, что..." или заверши вопросом "Правда или ложь?" в конце самого утверждения, чтобы было ясно, что нужно угадать. "answer" — строго строка "true" (если утверждение верно) или строка "false" (если утверждение ложно). Ложные утверждения делай так же правдоподобно, как верные — слегка исказив факт (неверное число, неверное имя, перепутанный порядок событий).
Из 3 карточек сделай минимум 1 карточку типа "blank" И минимум 1 карточку типа "truefalse" — никогда не делай все карточки одного типа.
Правила: числа, годы, даты — цифрами. Никакого markdown, никаких пояснений.
Ответь ТОЛЬКО валидным JSON-массивом ровно в этом формате:
[{"type":"blank","question":"...","answer":"..."},{"type":"truefalse","question":"...","answer":"true"},{"type":"truefalse","question":"...","answer":"false"}]`
    : `Create exactly 3 recall flashcards from this story — the most important, concrete facts (dates, numbers, names, definitions).
You MUST use both card types, not just one:
1. "blank" — a statement with a key word blanked out (write the blank as ___), "answer" is the missing word, 1-4 words
2. "truefalse" — a short statement based on the story (no more than 25 words including the intro phrase and "True or false?"), phrased as a self-contained riddle understandable with zero context (someone might see only this card, e.g. on social media, with no other information) — start with an intriguing framing like "Did you know..." or end the statement with "True or false?" so it's clear a guess is expected. "answer" is exactly the string "true" (if the statement is correct) or the string "false" (if incorrect). Make false statements just as plausible-sounding as true ones — slightly alter a fact (wrong number, wrong name, swapped order of events).
Out of the 3 cards, include at least 1 "blank" card AND at least 1 "truefalse" card — never make all cards the same type.
Rules: numbers, years, dates as digits. No markdown, no preamble.
Reply with ONLY a valid JSON array in exactly this shape:
[{"type":"blank","question":"...","answer":"..."},{"type":"truefalse","question":"...","answer":"true"},{"type":"truefalse","question":"...","answer":"false"}]`;

  const prompt = `${instr}

TITLE: ${title}

STORY:
${story.slice(0, 6000)}`;

  const authHeaders = await getAuthHeaders();
  const res = await fetch("/api/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 500,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error("flashcards_failed");
  const data = await res.json();
  const text = data.content.filter(b => b.type === "text").map(b => b.text).join("");
  const m = text.match(/\[[\s\S]*\]/);
  if (!m) throw new Error("flashcards_parse_failed");
  const cards = JSON.parse(m[0]);
  return Array.isArray(cards)
    ? cards
        .filter(c => c?.question && c?.answer)
        .map(c => ({
          type: c.type === "truefalse" ? "truefalse" : "blank",
          question: c.question,
          answer: c.type === "truefalse" ? (String(c.answer).toLowerCase() === "true" ? "true" : "false") : c.answer,
        }))
        .slice(0, 3)
    : [];
}